import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useContent } from "@/context/ContentContext";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { categories, contact } = useContent();
  const [form, setForm] = useState({ name: "", email: "", phone: "", session_type: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Your inquiry has been received. I'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", session_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 py-4 text-cream placeholder:text-muted2/60 focus:outline-none focus:border-gold transition-colors duration-500";

  return (
    <div data-testid="contact-page" className="pt-36 md:pt-48 pb-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-16">
          <div className="md:col-span-5">
            <Reveal>
              <p className="text-[0.65rem] uppercase tracking-[0.4em] text-gold mb-6">Inquire</p>
              <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-none">
                Tell me your <span className="italic text-beige">story.</span>
              </h1>
              <p className="mt-8 text-muted2 leading-relaxed max-w-md">
                Every collaboration begins with a conversation. Share a little about your vision, and I'll personally respond within two business days.
              </p>
              <div className="mt-12 space-y-3">
                <p className="text-[0.65rem] uppercase tracking-[0.35em] text-muted2">Studio</p>
                <p className="font-serif text-2xl">{contact.location}</p>
                <a href={`tel:${contact.phoneLink}`} data-testid="contact-phone" className="block text-cream/80 hover:text-gold transition-colors">{contact.phone}</a>
                <a href={`https://instagram.com/${contact.instagram}`} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="block text-cream/80 hover:text-gold transition-colors">@{contact.instagram}</a>
                <a href={`mailto:${contact.email}`} data-testid="contact-email" className="block text-cream/80 hover:text-gold transition-colors">{contact.email}</a>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <Reveal delay={0.15}>
              <form onSubmit={submit} data-testid="inquiry-form" className="space-y-10">
                <input data-testid="input-name" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} />
                <input data-testid="input-email" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)} />
                <input data-testid="input-phone" className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} />

                <div>
                  <select data-testid="select-session-type" className={inputCls + " cursor-pointer [&>option]:bg-ink2"} value={form.session_type} onChange={(e) => set("session_type", e.target.value)}>
                    <option value="">Session type</option>
                    {categories.map((c) => (
                      <option key={c.slug} value={c.title}>{c.title}</option>
                    ))}
                  </select>
                </div>

                <textarea data-testid="input-message" rows={4} className={inputCls + " resize-none"} placeholder="Tell me about your vision..." value={form.message} onChange={(e) => set("message", e.target.value)} />

                <motion.button
                  type="submit"
                  data-testid="send-inquiry-btn"
                  disabled={sending}
                  whileHover={{ scale: 1.01 }}
                  className="w-full border border-cream/40 py-5 text-xs uppercase tracking-[0.3em] text-cream hover:bg-cream hover:text-ink transition-colors duration-500 disabled:opacity-50"
                >
                  {sending ? "Sending..." : "Send Inquiry"}
                </motion.button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
